import React, {
  forwardRef,
  useCallback,
  useRef,
  useState,
} from 'react';
import type {
  ChangeEvent,
  ComponentType,
  ForwardedRef,
  KeyboardEvent,
  ReactElement,
  Ref,
  RefObject,
} from 'react';

import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { Counter, StyledInput, Wrapper } from './styled';
import type { TextAreaProps } from './types';

const handleTextAreaResize = (textarea: HTMLTextAreaElement) => {
  // that hack reset height of textarea
  textarea.style.height = 'auto';
  textarea.style.height = `${textarea.scrollHeight}px`;
};

function TextAreaInner<
  RefElementType,
  InputComponent extends
  | keyof JSX.IntrinsicElements
  | ComponentType<any> = 'input',
>(
  props: TextAreaProps<InputComponent>,
  ref: ForwardedRef<RefElementType>,
): ReactElement {
  const { debounceTimeout = 300, maxLength, counterClassName, ...inputProps } = props;
  const [count, setCount] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement | null>();
  const textareaRefCallback = useCallback((textarea: HTMLTextAreaElement | null) => {
    textareaRef.current = textarea;

    if (textarea) {
      handleTextAreaResize(textarea);
    }
  }, []);
  const composedRef = useComposedRefs(textareaRefCallback, ref as RefObject<HTMLTextAreaElement>);
  const onInput = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    setCount(e.target.value.length);

    handleTextAreaResize(e.target);
  }, []);
  const onKeyDown = useCallback((e: KeyboardEvent<HTMLTextAreaElement>) => {
    handleTextAreaResize(e.currentTarget);
  }, []);

  return (
    <Wrapper>
      <Counter className={counterClassName}>{maxLength && `${count}/${maxLength}`}</Counter>
      <StyledInput
        {...inputProps}
        component="textarea"
        ref={composedRef}
        debounceTimeout={debounceTimeout}
        maxLength={maxLength}
        onInput={onInput}
        onKeyDown={onKeyDown}
      />
    </Wrapper>
  );
}

export const TextArea = forwardRef(TextAreaInner) as <
  RefElementType,
  InputComponent extends
  | keyof JSX.IntrinsicElements
  | ComponentType<any> = 'input',
>(
  props: TextAreaProps<InputComponent> & {
    ref?: Ref<RefElementType>;
  }
) => ReactElement;
